(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const config = window.lymiaAstroSecretConfig || {};
  const scriptSrc = document.currentScript?.src || "";
  const secretCode = config.code || "STARLIGHT";
  const secretStatusUrl = config.statusUrl || "";
  const secretStatusHeaders = config.statusHeaders || {};
  const secretStatusTimeout = Number(config.statusTimeoutMs || 4800);
  const getDefaultSecretClaimUrl = (url) => {
    if (!url) {
      return "";
    }

    try {
      const parsed = new URL(url, window.location.href);

      parsed.hash = "";
      parsed.search = "";
      parsed.pathname = parsed.pathname.replace(/\/latest\/?$/, "");

      return parsed.href;
    } catch (error) {
      return "";
    }
  };
  const secretClaimUrl = config.claimUrl || config.statusWriteUrl || getDefaultSecretClaimUrl(secretStatusUrl);
  const secretClaimHeaders = config.claimHeaders || config.writeHeaders || secretStatusHeaders;
  const secretClaimTimeout = Number(config.claimTimeoutMs || secretStatusTimeout);
  const secretClaimMethod = config.claimMethod || "PUT";
  const secretClaimPayload = config.claimPayload ?? config.claimBody ?? { available: false };
  const reducedStorageKey = "lymiaAstroReduced";
  const readReducedPreference = () => {
    try {
      const stored = window.localStorage.getItem(reducedStorageKey);

      if (stored === "true") {
        return true;
      }

      if (stored === "false") {
        return false;
      }
    } catch (error) {
      // Storage can be unavailable in strict privacy modes.
    }

    return reduceMotion.matches;
  };
  const saveReducedPreference = (enabled) => {
    try {
      window.localStorage.setItem(reducedStorageKey, enabled ? "true" : "false");
    } catch (error) {
      // Keep the session toggle usable even when persistence is blocked.
    }
  };

  const getAssetUrl = (filename) => {
    if (!scriptSrc) {
      return filename;
    }

    const base = new URL(scriptSrc);
    base.search = "";
    return new URL(filename, base.href).href;
  };

  const secretMusicUrl = config.musicUrl || getAssetUrl("astro.mp3");
  const astroLogoUrl = config.logoUrl || config.astroLogoUrl || getAssetUrl("lymia.art.astro.png");
  const astroInstagramUrl = config.instagramUrl || "https://www.instagram.com/lymia.art/";
  const astroIconBoxImages = [
    {
      selector: "#sp-icon_box_cVzahD img",
      filename: "Livraison.png",
      alt: "Livraison ajustee Astro",
    },
    {
      selector: "#sp-icon_box_MKGgpb img",
      filename: "Paiement.png",
      alt: "Paiement securise Astro",
    },
    {
      selector: "#sp-icon_box_TfhTh4 img",
      filename: "Emballage.png",
      alt: "Emballage Astro",
    },
    {
      selector: "#sp-icon_box_rPLECL img",
      filename: "Supprt.png",
      alt: "Support Astro",
    },
  ];

  if (document.getElementById("lymia-astro-canvas")) {
    return;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { alpha: true });

  if (!context) {
    return;
  }

  canvas.id = "lymia-astro-canvas";
  canvas.setAttribute("aria-hidden", "true");
  document.documentElement.classList.add("lymia-astro-js-enabled");
  document.body.prepend(canvas);

  let width = 0;
  let height = 0;
  let ratio = 1;
  let stars = [];
  let shootingStars = [];
  let lastFrame = 0;
  let shootingTimer = 0;
  let nextShootingStarDelay = 0;
  let activeSecretAudio = null;
  let shootingStarHitId = 0;
  let shootingStarShowerActive = false;
  let renderFrame = 0;
  let astroReduced = readReducedPreference();
  let performanceToggle = null;

  document.documentElement.classList.toggle("lymia-astro-reduced", astroReduced);

  const palette = [
    "255, 255, 255",
    "255, 243, 199",
    "255, 182, 220",
    "198, 178, 255",
    "185, 232, 255",
  ];
  const glyphs = ["\u2726", "\u2727", "\u2605", "\u2606"];
  const cartGlyphs = ["\u2726", "\u2727", "\u2605", "\u22c6"];
  const cardClickGlyphs = ["\u2726", "\u2727", "\u2605", "\u2606", "\u22c6"];
  const cardClickColors = [
    "var(--lymia-astro-lavender)",
    "var(--lymia-astro-baby-blue)",
    "var(--lymia-astro-rose)",
    "rgba(255, 255, 255, 0.96)",
  ];
  const cardEdgeStarGlyphs = ["\u2726", "\u2727", "\u2605", "\u2606"];
  const cardEdgeStarColors = [
    "var(--lymia-astro-starlight)",
    "var(--lymia-astro-rose)",
    "var(--lymia-astro-baby-blue)",
    "var(--lymia-astro-lavender)",
    "rgba(255, 255, 255, 0.92)",
  ];
  const cardEdgeStarPattern = [
    { left: 4.5, top: 9, size: 0.82, scale: 0.86, rotation: -14, opacity: 0.72, speed: 1.9, turn: 160 },
    { left: 17, top: 2.6, size: 0.58, scale: 0.78, rotation: 18, opacity: 0.48, speed: 2.4, turn: -210 },
    { left: 38, top: 3.2, size: 0.64, scale: 0.82, rotation: -34, opacity: 0.58, speed: 2.1, turn: 190 },
    { left: 63, top: 2.4, size: 0.52, scale: 0.76, rotation: 44, opacity: 0.46, speed: 2.7, turn: -170 },
    { left: 91.5, top: 7.5, size: 0.76, scale: 0.88, rotation: 9, opacity: 0.62, speed: 2.2, turn: 220 },
    { left: 97, top: 28, size: 0.54, scale: 0.78, rotation: -24, opacity: 0.46, speed: 2.5, turn: -190 },
    { left: 98.2, top: 54, size: 0.72, scale: 0.9, rotation: 38, opacity: 0.58, speed: 1.8, turn: 180 },
    { left: 95, top: 81, size: 0.5, scale: 0.76, rotation: -42, opacity: 0.42, speed: 2.8, turn: -220 },
    { left: 84, top: 97, size: 0.68, scale: 0.82, rotation: 20, opacity: 0.54, speed: 2.3, turn: 200 },
    { left: 58, top: 98.4, size: 0.48, scale: 0.74, rotation: -18, opacity: 0.42, speed: 2.6, turn: -180 },
    { left: 31, top: 96.8, size: 0.7, scale: 0.86, rotation: 32, opacity: 0.56, speed: 2, turn: 210 },
    { left: 9, top: 91.5, size: 0.56, scale: 0.78, rotation: -8, opacity: 0.48, speed: 2.7, turn: -190 },
    { left: 2.8, top: 65, size: 0.64, scale: 0.84, rotation: 28, opacity: 0.52, speed: 2.2, turn: 170 },
    { left: 2, top: 36, size: 0.5, scale: 0.74, rotation: -36, opacity: 0.42, speed: 2.9, turn: -210 },
  ];

  const random = (min, max) => Math.random() * (max - min) + min;
  const delay = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));
  const setNextShootingStarDelay = () => {
    nextShootingStarDelay = random(16, 30);
  };
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const isAstroReduced = () => astroReduced || reduceMotion.matches;
  const createBackgroundStar = () => ({
    x: random(0, width),
    y: random(0, height),
    size: random(0.7, 2.7),
    glow: random(0.2, 0.95),
    speed: random(0.16, 0.68),
    phase: random(0, Math.PI * 2),
    drift: random(-0.018, 0.018),
    color: palette[Math.floor(random(0, palette.length))],
    kind: Math.random() < 0.24 ? "glyph" : "dot",
    glyph: glyphs[Math.floor(random(0, glyphs.length))],
  });

  const secretStarUnsafeSelector = [
    "a",
    "button",
    "input",
    "select",
    "textarea",
    "label",
    "summary",
    "iframe",
    "[role='button']",
    "[role='link']",
    "[role='checkbox']",
    "[role='menuitem']",
    "[tabindex]:not([tabindex='-1'])",
    ".button",
    ".shopify-payment-button",
    ".header",
    ".header-wrapper",
    ".announcement-bar",
    ".utility-bar",
    ".menu-drawer",
    ".search-modal",
    ".facets",
    ".facet-filters",
    ".product-form",
    ".product-card-wrapper",
    ".collection-card-wrapper",
    ".card",
    ".cart",
    ".cart-item",
    ".quantity",
  ].join(",");

  const isUnsafeSecretStarTarget = (left, top, secretStar = null) => {
    const samples = [
      [0, 0],
      [-22, 0],
      [22, 0],
      [0, -22],
      [0, 22],
      [-16, -16],
      [16, 16],
    ];
    const previousPointerEvents = secretStar?.style.pointerEvents;

    if (secretStar) {
      secretStar.style.pointerEvents = "none";
    }

    try {
      for (const [offsetX, offsetY] of samples) {
        const x = left + offsetX;
        const y = top + offsetY;

        if (x < 0 || y < 0 || x > width || y > height) {
          return true;
        }

        const element = document.elementFromPoint(x, y);

        if (!element || element.closest(".lymia-astro-secret-reveal")) {
          return true;
        }

        if (element.closest(secretStarUnsafeSelector)) {
          return true;
        }

        let current = element;

        while (current && current !== document.body) {
          if (window.getComputedStyle(current).cursor === "pointer") {
            return true;
          }

          current = current.parentElement;
        }
      }
    } finally {
      if (secretStar) {
        secretStar.style.pointerEvents = previousPointerEvents || "";
      }
    }

    return false;
  };

  const getSafeSecretStarPosition = (secretStar = null) => {
    const minLeft = 42;
    const maxLeft = Math.max(52, width - 52);
    const minTop = 118;
    const maxTop = Math.max(132, height - 58);

    for (let attempt = 0; attempt < 90; attempt += 1) {
      const left = random(minLeft, maxLeft);
      const top = random(minTop, maxTop);

      if (!isUnsafeSecretStarTarget(left, top, secretStar)) {
        return { left, top };
      }
    }

    const fallbackPositions = [
      { left: width - 50, top: height * 0.36 },
      { left: 50, top: height * 0.56 },
      { left: width - 54, top: height - 66 },
      { left: 54, top: height - 72 },
      { left: width * 0.5, top: height - 62 },
    ].map((position) => ({
      left: clamp(position.left, minLeft, maxLeft),
      top: clamp(position.top, minTop, maxTop),
    }));

    return fallbackPositions.find((position) => !isUnsafeSecretStarTarget(position.left, position.top, secretStar))
      || fallbackPositions[0];
  };

  const applyAstroLogo = () => {
    if (!astroLogoUrl) {
      return;
    }

    const logos = document.querySelectorAll(".header__heading-logo-wrapper img, img.header__heading-logo");

    for (const logo of logos) {
      if (!(logo instanceof HTMLImageElement) || logo.dataset.lymiaAstroLogoApplied === "true") {
        continue;
      }

      const picture = logo.closest("picture");

      if (picture) {
        picture.querySelectorAll("source").forEach((source) => {
          source.srcset = astroLogoUrl;
        });
      }

      logo.dataset.lymiaAstroLogoApplied = "true";
      logo.src = astroLogoUrl;
      logo.srcset = astroLogoUrl;
      logo.alt = logo.alt || "Lymia Art Astro";
    }
  };

  const applyAstroIconBoxImages = () => {
    astroIconBoxImages.forEach(({ selector, filename, alt }) => {
      const image = document.querySelector(selector);

      if (!(image instanceof HTMLImageElement)) {
        return;
      }

      const url = getAssetUrl(filename);

      if (image.dataset.lymiaAstroIcon === filename) {
        return;
      }

      image.dataset.lymiaAstroIcon = filename;
      image.src = url;
      image.srcset = url;
      image.dataset.src = url;
      image.alt = alt;
      image.classList.add("entered", "loaded");
      image.setAttribute("data-ll-status", "loaded");
    });
  };

  const applyAstroInstagramIntro = () => {
    const section = document.querySelector("#shopify-section-template--28897916354887__1708509915bff469ad");

    if (!(section instanceof HTMLElement) || section.querySelector(".lymia-astro-instagram-intro")) {
      return;
    }

    const appBlock = section.querySelector(".shopify-block.shopify-app-block");
    const feed = section.querySelector("#rap__med__instagram, #rap__med__insta");
    const target = appBlock || feed;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    const intro = document.createElement("a");

    intro.className = "lymia-astro-instagram-intro";
    intro.href = astroInstagramUrl;
    intro.target = "_blank";
    intro.rel = "noopener noreferrer";
    intro.setAttribute("aria-label", "Suivre Lymia Art sur Instagram");
    intro.innerHTML = `
      <span class="lymia-astro-instagram-orbit" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="4.8"></rect>
          <circle cx="12" cy="12" r="3.55"></circle>
          <circle cx="16.95" cy="7.05" r="0.78"></circle>
        </svg>
      </span>
      <span class="lymia-astro-instagram-copy">
        <span>Suivez mes aventures astro-ordinaires sur Instagram !</span>
      </span>
    `;

    target.insertAdjacentElement("beforebegin", intro);
  };

  const updatePerformanceToggle = () => {
    if (!performanceToggle) {
      return;
    }

    const label = performanceToggle.querySelector(".lymia-astro-performance-toggle__label");
    const enabled = isAstroReduced();

    performanceToggle.setAttribute("aria-pressed", enabled ? "true" : "false");
    performanceToggle.title = enabled
      ? "Animations Astro reduites. Cliquer pour reactiver les animations."
      : "Cliquer pour activer le mode Astro reduit.";

    if (label) {
      label.innerHTML = enabled ? "Astro r&eacute;duit" : "Astro anim&eacute;";
    }
  };

  const installPerformanceToggle = () => {
    const grid = document.querySelector(
      "#shopify-section-sections--28897916715335__announcement-bar .utility-bar__grid, .utility-bar__grid",
    );

    if (!(grid instanceof HTMLElement)) {
      return;
    }

    const existing = grid.querySelector(".lymia-astro-performance-toggle");

    if (existing instanceof HTMLButtonElement) {
      performanceToggle = existing;
      updatePerformanceToggle();
      return;
    }

    const slot = grid.querySelector(".localization-wrapper") || grid;
    const button = document.createElement("button");

    button.type = "button";
    button.className = "lymia-astro-performance-toggle";
    button.innerHTML = `
      <span class="lymia-astro-performance-toggle__icon" aria-hidden="true">&#10022;</span>
      <span class="lymia-astro-performance-toggle__label"></span>
    `;
    button.addEventListener("click", () => {
      setAstroReducedMode(!astroReduced);
    });

    performanceToggle = button;

    if (slot instanceof HTMLElement) {
      slot.classList.add("lymia-astro-performance-toggle-slot");
      slot.appendChild(button);
    }

    updatePerformanceToggle();
  };

  let cardDecorationTimer = 0;

  const decorateProductCards = () => {
    const cards = document.querySelectorAll(".product-card-wrapper .card, .collection-card-wrapper .card");

    cards.forEach((card, cardIndex) => {
      if (!(card instanceof HTMLElement) || card.dataset.lymiaAstroCardStars === "true") {
        return;
      }

      card.dataset.lymiaAstroCardStars = "true";

      cardEdgeStarPattern.forEach((point, index) => {
        const star = document.createElement("span");
        const cardOffset = (cardIndex % 3) - 1;
        const left = clamp(point.left + cardOffset * (index % 2 ? 0.7 : -0.45), 1.4, 98.6);
        const top = clamp(point.top + cardOffset * (index % 3 ? -0.45 : 0.6), 1.4, 98.6);

        star.className = "lymia-astro-card-edge-star";
        star.setAttribute("aria-hidden", "true");
        star.textContent = cardEdgeStarGlyphs[(index + cardIndex) % cardEdgeStarGlyphs.length];
        star.style.setProperty("--edge-star-left", `${left}%`);
        star.style.setProperty("--edge-star-top", `${top}%`);
        star.style.setProperty("--edge-star-color", cardEdgeStarColors[(index + cardIndex) % cardEdgeStarColors.length]);
        star.style.setProperty("--edge-star-size", `${point.size}rem`);
        star.style.setProperty("--edge-star-scale", point.scale);
        star.style.setProperty("--edge-star-opacity", point.opacity);
        star.style.setProperty("--edge-star-rotation", `${point.rotation + cardIndex * 9}deg`);
        star.style.setProperty("--edge-star-speed", `${point.speed}s`);
        star.style.setProperty("--edge-star-turn", `${point.turn}deg`);
        card.appendChild(star);
      });
    });
  };

  const scheduleProductCardDecoration = () => {
    window.clearTimeout(cardDecorationTimer);
    cardDecorationTimer = window.setTimeout(decorateProductCards, 90);
  };

  const resize = () => {
    const nextRatio = Math.min(window.devicePixelRatio || 1, 2);
    const nextWidth = window.innerWidth;
    const nextHeight = window.innerHeight;

    if (nextRatio === ratio && nextWidth === width && nextHeight === height) {
      return;
    }

    const previousWidth = width;
    const previousHeight = height;

    ratio = nextRatio;
    width = nextWidth;
    height = nextHeight;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    const density = Math.min(270, Math.max(118, Math.floor((width * height) / 8600)));

    if (stars.length && previousWidth && previousHeight) {
      const scaleX = Math.abs(width - previousWidth) > 96 ? width / previousWidth : 1;
      const scaleY = Math.abs(height - previousHeight) > 180 ? height / previousHeight : 1;

      for (const star of stars) {
        star.x = clamp(star.x * scaleX, 0, width);
        star.y = clamp(star.y * scaleY, 0, height);
      }

      if (stars.length > density) {
        stars.length = density;
      }
    }

    while (stars.length < density) {
      stars.push(createBackgroundStar());
    }

    const secretStar = document.querySelector(".lymia-astro-secret-star");
    if (secretStar) {
      clampSecretStar(secretStar);
    }

    if (isAstroReduced()) {
      renderStaticSky();
    }
  };

  const triggerShootingStarShower = (originX = width * 0.5, originY = height * 0.28) => {
    if (shootingStarShowerActive || isAstroReduced()) {
      return;
    }

    shootingStarShowerActive = true;

    const shower = document.createElement("canvas");
    const showerContext = shower.getContext("2d", { alpha: true });

    shower.className = "lymia-astro-shooting-star-shower";
    shower.setAttribute("aria-hidden", "true");

    if (!showerContext) {
      shootingStarShowerActive = false;
      return;
    }

    const showerRatio = Math.min(window.devicePixelRatio || 1, 2);
    const showerWidth = window.innerWidth;
    const showerHeight = window.innerHeight;
    const showerStars = Array.from({ length: 7 }, (_, index) => {
      const scale = index === 2 || index === 5 ? random(1.85, 2.25) : random(1.25, 1.8);

      return {
        x: random(showerWidth * 0.62, showerWidth * 1.22) + index * 36,
        y: random(12, showerHeight * 0.48),
        length: random(210, 310) * scale,
        speed: random(390, 560) * scale,
        life: 0,
        ttl: random(1.45, 2.1),
        delay: index * 0.18 + random(0, 0.1),
        slope: random(0.26, 0.4),
        scale,
        color: index % 2 ? "255, 182, 220" : "255, 243, 199",
        accent: index % 3 ? "185, 232, 255" : "198, 178, 255",
        glyph: glyphs[index % 3],
      };
    });
    let showerStart = 0;

    shower.width = Math.floor(showerWidth * showerRatio);
    shower.height = Math.floor(showerHeight * showerRatio);
    shower.style.width = `${showerWidth}px`;
    shower.style.height = `${showerHeight}px`;
    showerContext.setTransform(showerRatio, 0, 0, showerRatio, 0, 0);

    document.body.appendChild(shower);

    const drawLargeShootingStar = (star, elapsed) => {
      const life = elapsed - star.delay;

      if (life <= 0 || life > star.ttl) {
        return;
      }

      const progress = life / star.ttl;
      const alpha = Math.sin(Math.min(progress, 1) * Math.PI);
      const distance = star.speed * life;
      const x = star.x - distance;
      const y = star.y + distance * star.slope;
      const tailX = x + star.length;
      const tailY = y - star.length * star.slope;
      const gradient = showerContext.createLinearGradient(x, y, tailX, tailY);
      const haloRadius = 64 * star.scale;

      gradient.addColorStop(0, `rgba(${star.color}, ${0.92 * alpha})`);
      gradient.addColorStop(0.16, `rgba(${star.accent}, ${0.58 * alpha})`);
      gradient.addColorStop(0.38, `rgba(${star.color}, ${0.28 * alpha})`);
      gradient.addColorStop(1, `rgba(${star.color}, 0)`);

      showerContext.save();
      showerContext.globalCompositeOperation = "lighter";

      const halo = showerContext.createRadialGradient(x, y, 0, x, y, haloRadius);

      halo.addColorStop(0, `rgba(255, 255, 255, ${0.45 * alpha})`);
      halo.addColorStop(0.18, `rgba(${star.color}, ${0.5 * alpha})`);
      halo.addColorStop(0.48, `rgba(${star.accent}, ${0.18 * alpha})`);
      halo.addColorStop(1, `rgba(${star.color}, 0)`);

      showerContext.fillStyle = halo;
      showerContext.beginPath();
      showerContext.arc(x, y, haloRadius, 0, Math.PI * 2);
      showerContext.fill();

      showerContext.lineCap = "round";
      showerContext.strokeStyle = gradient;
      showerContext.lineWidth = 2.9 * star.scale;
      showerContext.shadowColor = `rgba(${star.color}, ${0.9 * alpha})`;
      showerContext.shadowBlur = 24 * star.scale;
      showerContext.beginPath();
      showerContext.moveTo(x, y);
      showerContext.lineTo(tailX, tailY);
      showerContext.stroke();

      showerContext.fillStyle = `rgba(255, 255, 255, ${0.94 * alpha})`;
      showerContext.shadowColor = `rgba(${star.color}, ${0.95 * alpha})`;
      showerContext.shadowBlur = 18 * star.scale;
      showerContext.font = `${Math.round(15 * star.scale)}px serif`;
      showerContext.textAlign = "center";
      showerContext.textBaseline = "middle";
      showerContext.fillText(star.glyph, x, y);
      showerContext.restore();
    };

    const renderShower = (timestamp) => {
      if (isAstroReduced() || !document.body.contains(shower)) {
        shower.remove();
        shootingStarShowerActive = false;
        return;
      }

      if (!showerStart) {
        showerStart = timestamp;
      }

      const elapsed = (timestamp - showerStart) / 1000;

      showerContext.clearRect(0, 0, showerWidth, showerHeight);

      for (const star of showerStars) {
        drawLargeShootingStar(star, elapsed);
      }

      if (elapsed < 3.4) {
        requestAnimationFrame(renderShower);
        return;
      }

      shower.remove();
      shootingStarShowerActive = false;
    };

    requestAnimationFrame(renderShower);
  };

  const removeShootingStarHit = (shootingStar) => {
    if (shootingStar.hitElement) {
      shootingStar.hitElement.remove();
      shootingStar.hitElement = null;
    }
  };

  const createShootingStarHit = (shootingStar) => {
    const button = document.createElement("button");

    button.type = "button";
    button.className = "lymia-astro-shooting-star-hit";
    button.setAttribute("aria-label", "Etoile filante magique");
    button.style.setProperty("--shooting-hit-hue", `rgba(${shootingStar.color}, 0.92)`);

    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (shootingStar.clicked) {
        return;
      }

      shootingStar.clicked = true;
      triggerShootingStarShower(shootingStar.currentX || width * 0.5, shootingStar.currentY || height * 0.28);
      removeShootingStarHit(shootingStar);
    });

    document.body.appendChild(button);
    shootingStar.hitElement = button;
  };

  const updateShootingStarHit = (shootingStar, x, y, alpha) => {
    shootingStar.currentX = x;
    shootingStar.currentY = y;

    if (!shootingStar.hitElement && !shootingStar.clicked) {
      createShootingStarHit(shootingStar);
    }

    if (!shootingStar.hitElement) {
      return;
    }

    const isVisible = x > -110 && x < width + 110 && y > -110 && y < height + 110 && alpha > 0.12;

    shootingStar.hitElement.hidden = !isVisible;
    shootingStar.hitElement.style.left = `${x}px`;
    shootingStar.hitElement.style.top = `${y}px`;
    shootingStar.hitElement.style.setProperty("--shooting-hit-opacity", `${clamp(alpha, 0.18, 0.92)}`);
  };

  const createShootingStar = () => {
    if (isAstroReduced()) {
      return;
    }

    shootingStarHitId += 1;

    shootingStars.push({
      id: shootingStarHitId,
      x: random(width * 0.48, width * 1.16),
      y: random(10, height * 0.36),
      length: random(220, 360),
      speed: random(315, 440),
      life: 0,
      ttl: random(2.05, 2.78),
      color: Math.random() > 0.48 ? "255, 182, 220" : "255, 243, 199",
      accent: Math.random() > 0.5 ? "198, 178, 255" : "185, 232, 255",
      glyph: glyphs[Math.floor(random(0, 3))],
      hitElement: null,
      clicked: false,
    });
  };

  const drawStar = (star, time, staticMode = false) => {
    const pulse = staticMode ? 0.42 + star.glow * 0.28 : (Math.sin(time * star.speed + star.phase) + 1) / 2;
    const alpha = 0.18 + pulse * 0.75;
    const radius = star.size + pulse * star.glow;

    if (!staticMode) {
      star.x += star.drift;
    }

    if (star.x < -2) star.x = width + 2;
    if (star.x > width + 2) star.x = -2;

    context.shadowColor = `rgba(${star.color}, ${alpha * 0.72})`;
    context.shadowBlur = 8 + pulse * 10;

    if (star.kind === "glyph") {
      context.save();
      context.translate(star.x, star.y);
      context.rotate(staticMode ? star.phase * 0.08 : Math.sin(time * 0.18 + star.phase) * 0.2);
      context.fillStyle = `rgba(${star.color}, ${alpha * 0.82})`;
      context.font = `${Math.max(8, radius * 7.7)}px serif`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(star.glyph, 0, 0);
      context.restore();
    } else {
      context.beginPath();
      context.arc(star.x, star.y, radius, 0, Math.PI * 2);
      context.fillStyle = `rgba(${star.color}, ${alpha})`;
      context.fill();
    }

    if (star.size > 1.35 && pulse > 0.64) {
      context.save();
      context.translate(star.x, star.y);
      context.rotate(star.phase);
      context.strokeStyle = `rgba(${star.color}, ${alpha * 0.32})`;
      context.lineWidth = 0.7;
      context.beginPath();
      context.moveTo(-5, 0);
      context.lineTo(5, 0);
      context.moveTo(0, -5);
      context.lineTo(0, 5);
      context.stroke();
      context.restore();
    }
  };

  const renderStaticSky = () => {
    context.clearRect(0, 0, width, height);
    context.shadowBlur = 0;

    for (const star of stars) {
      drawStar(star, 0, true);
    }
  };

  const stopDynamicAstro = () => {
    if (renderFrame) {
      window.cancelAnimationFrame(renderFrame);
      renderFrame = 0;
    }

    for (const shootingStar of shootingStars) {
      removeShootingStarHit(shootingStar);
    }

    shootingStars = [];
    shootingTimer = 0;
    shootingStarShowerActive = false;
    document.querySelectorAll(
      ".lymia-astro-shooting-star-hit, .lymia-astro-shooting-star-shower, .lymia-astro-cart-sparkles, .lymia-astro-card-sparkles",
    ).forEach((element) => element.remove());
  };

  const startDynamicAstro = () => {
    if (isAstroReduced() || renderFrame) {
      return;
    }

    lastFrame = performance.now();
    renderFrame = window.requestAnimationFrame(render);
  };

  const setAstroReducedMode = (enabled, persist = true) => {
    astroReduced = Boolean(enabled);
    document.documentElement.classList.toggle("lymia-astro-reduced", astroReduced);

    if (persist) {
      saveReducedPreference(astroReduced);
    }

    updatePerformanceToggle();

    if (astroReduced) {
      stopDynamicAstro();
      renderStaticSky();
      return;
    }

    setNextShootingStarDelay();
    startDynamicAstro();
  };

  const parseSecretState = (data) => {
    const payload = data && typeof data === "object" && data.record ? data.record : data;

    if (!payload || typeof payload !== "object") {
      return false;
    }

    const rawValue =
      payload.available ??
      payload.active ??
      payload.enabled ??
      payload.open ??
      payload.isOpen ??
      payload.isAvailable ??
      payload.state ??
      payload.status;

    if (typeof rawValue === "boolean") {
      return rawValue;
    }

    if (typeof rawValue === "number") {
      return rawValue > 0;
    }

    if (typeof rawValue === "string") {
      const normalized = rawValue.trim().toLowerCase();

      if (["available", "active", "enabled", "open", "on", "true", "yes", "unclaimed", "winner"].includes(normalized)) {
        return true;
      }

      if (["unavailable", "inactive", "disabled", "closed", "off", "false", "no", "claimed", "lost"].includes(normalized)) {
        return false;
      }
    }

    return false;
  };

  const fetchSecretState = async () => {
    if (!secretStatusUrl) {
      return {
        state: "error",
        reason: "missing-status-url",
      };
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), secretStatusTimeout);

    try {
      const response = await fetch(secretStatusUrl, {
        cache: "no-store",
        headers: secretStatusHeaders,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Status request failed: ${response.status}`);
      }

      const data = await response.json();

      return {
        state: parseSecretState(data) ? "won" : "lost",
        data,
      };
    } catch (error) {
      return {
        state: "error",
        reason: error instanceof Error ? error.message : "unknown-error",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const createSecretClaimBody = (data) => {
    if (typeof secretClaimPayload === "function") {
      const payload = secretClaimPayload(data);

      return typeof payload === "string" ? payload : JSON.stringify(payload);
    }

    return typeof secretClaimPayload === "string" ? secretClaimPayload : JSON.stringify(secretClaimPayload);
  };

  const claimSecretState = async (data = null) => {
    if (!secretClaimUrl || config.claimOnWin === false) {
      return {
        ok: false,
        skipped: true,
      };
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), secretClaimTimeout);

    try {
      const response = await fetch(secretClaimUrl, {
        method: secretClaimMethod,
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          ...secretClaimHeaders,
        },
        body: createSecretClaimBody(data),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Claim request failed: ${response.status}`);
      }

      console.info("Etoile secrete marquee comme deja reclamee.");

      return {
        ok: true,
      };
    } catch (error) {
      console.info("Impossible de marquer l'etoile comme deja reclamee.", error);

      return {
        ok: false,
        reason: error instanceof Error ? error.message : "unknown-error",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const playSecretMusic = async (retryButton = null) => {
    stopSecretMusic();

    if (!secretMusicUrl) {
      return false;
    }

    activeSecretAudio = document.createElement("audio");
    activeSecretAudio.loop = true;
    activeSecretAudio.preload = "auto";
    activeSecretAudio.volume = 0.58;
    activeSecretAudio.playsInline = true;
    activeSecretAudio.src = secretMusicUrl;
    activeSecretAudio.setAttribute("aria-hidden", "true");
    activeSecretAudio.className = "lymia-astro-secret-music";
    document.body.appendChild(activeSecretAudio);

    try {
      await activeSecretAudio.play();

      if (retryButton) {
        retryButton.hidden = true;
      }

      return true;
    } catch (error) {
      stopSecretMusic();

      if (retryButton) {
        retryButton.hidden = false;
      }

      console.info("La musique Astro a ete bloquee par le navigateur. Clique sur le bouton d'activation dans la popup.", error);
      return false;
    }
  };

  const stopSecretMusic = () => {
    if (!activeSecretAudio) {
      return;
    }

    activeSecretAudio.pause();

    try {
      activeSecretAudio.currentTime = 0;
    } catch (error) {
      // Some browsers reject currentTime changes when the source never loaded.
    }

    activeSecretAudio.remove();
    activeSecretAudio = null;
  };

  const testSecretMusic = async () => {
    const tester = document.createElement("audio");

    tester.preload = "metadata";
    tester.src = secretMusicUrl;

    console.info("URL musique Astro:", secretMusicUrl);
    console.info("Support MP3 navigateur:", tester.canPlayType("audio/mpeg") || "non declare");

    try {
      await tester.play();
      tester.pause();
      tester.currentTime = 0;
      console.info("Test musique Astro: OK");
      return true;
    } catch (error) {
      console.info("Test musique Astro: echec", error);
      return false;
    }
  };

  const createRevealStar = (container) => {
    const star = document.createElement("span");
    star.style.setProperty("--x", `${random(0, 100)}%`);
    star.style.setProperty("--y", `${random(0, 100)}%`);
    star.style.setProperty("--size", `${random(0.55, 1.65)}rem`);
    star.style.setProperty("--delay", `${random(-4, 1)}s`);
    star.style.setProperty("--duration", `${random(2.4, 5.2)}s`);
    star.style.setProperty("--hue", Math.random() > 0.55 ? "var(--lymia-astro-rose)" : "var(--lymia-astro-starlight)");
    star.textContent = glyphs[Math.floor(random(0, glyphs.length))];
    container.appendChild(star);
  };

  const createBurstStar = (container) => {
    const star = document.createElement("span");
    const startAngle = random(0, Math.PI * 2);
    const startDistance = random(3, 34);
    const endAngle = startAngle + random(-0.62, 0.62);
    const endDistance = random(96, 145);
    const startX = Math.cos(startAngle) * startDistance;
    const startY = Math.sin(startAngle) * startDistance;
    const endX = Math.cos(endAngle) * endDistance;
    const endY = Math.sin(endAngle) * endDistance;

    star.style.setProperty("--start-x", `${startX}vw`);
    star.style.setProperty("--start-y", `${startY}vh`);
    star.style.setProperty("--end-x", `${endX}vw`);
    star.style.setProperty("--end-y", `${endY}vh`);
    star.style.setProperty("--size", `${random(4.8, 13)}rem`);
    star.style.setProperty("--delay", `${random(0, 0.3)}s`);
    star.style.setProperty("--rotate", `${random(-30, 30)}deg`);
    star.style.setProperty("--hue", Math.random() > 0.5 ? "var(--lymia-astro-rose)" : "var(--lymia-astro-starlight)");
    star.textContent = glyphs[Math.floor(random(0, 3))];
    container.appendChild(star);
  };

  const createCartSparkleBurst = (button, event) => {
    if (isAstroReduced() || button.disabled || button.getAttribute("aria-disabled") === "true") {
      return;
    }

    const rect = button.getBoundingClientRect();
    const centerX = event?.clientX || rect.left + rect.width / 2;
    const centerY = event?.clientY || rect.top + rect.height / 2;
    const burst = document.createElement("span");

    burst.className = "lymia-astro-cart-sparkles";
    burst.setAttribute("aria-hidden", "true");
    burst.style.left = `${centerX}px`;
    burst.style.top = `${centerY}px`;

    for (let index = 0; index < 20; index += 1) {
      const sparkle = document.createElement("span");
      const angle = (Math.PI * 2 * index) / 20 + random(-0.24, 0.24);
      const distance = random(34, 88);
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      sparkle.textContent = cartGlyphs[Math.floor(random(0, cartGlyphs.length))];
      sparkle.style.setProperty("--x", `${x}px`);
      sparkle.style.setProperty("--y", `${y}px`);
      sparkle.style.setProperty("--size", `${random(0.85, 1.55)}rem`);
      sparkle.style.setProperty("--delay", `${random(0, 0.08)}s`);
      sparkle.style.setProperty("--rotate", `${random(-26, 26)}deg`);
      burst.appendChild(sparkle);
    }

    document.body.appendChild(burst);
    button.classList.add("lymia-astro-cart-button-pop");

    window.setTimeout(() => {
      burst.remove();
      button.classList.remove("lymia-astro-cart-button-pop");
    }, 980);
  };

  const getProductCardLink = (card) => card.querySelector(
    ".card__heading a[href], a.full-unstyled-link[href], a[href*='/products/']",
  );

  const createProductCardSparkleBurst = (card, event) => {
    if (isAstroReduced()) {
      return;
    }

    const rect = card.getBoundingClientRect();
    const centerX = event?.clientX || rect.left + rect.width / 2;
    const centerY = event?.clientY || rect.top + rect.height / 2;
    const burst = document.createElement("span");

    burst.className = "lymia-astro-card-sparkles";
    burst.setAttribute("aria-hidden", "true");
    burst.style.left = `${centerX}px`;
    burst.style.top = `${centerY}px`;

    for (let index = 0; index < 24; index += 1) {
      const sparkle = document.createElement("span");
      const angle = (Math.PI * 2 * index) / 24 + random(-0.28, 0.28);
      const distance = random(38, 108);
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      sparkle.textContent = cardClickGlyphs[Math.floor(random(0, cardClickGlyphs.length))];
      sparkle.style.setProperty("--x", `${x}px`);
      sparkle.style.setProperty("--y", `${y}px`);
      sparkle.style.setProperty("--size", `${random(0.76, 1.42)}rem`);
      sparkle.style.setProperty("--delay", `${random(0, 0.1)}s`);
      sparkle.style.setProperty("--rotate", `${random(-34, 34)}deg`);
      sparkle.style.setProperty("--hue", cardClickColors[Math.floor(random(0, cardClickColors.length))]);
      burst.appendChild(sparkle);
    }

    document.body.appendChild(burst);
    card.classList.add("lymia-astro-card-pop");

    window.setTimeout(() => {
      burst.remove();
      card.classList.remove("lymia-astro-card-pop");
    }, 980);
  };

  const handleProductCardClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element) || event.defaultPrevented || event.button !== 0) {
      return;
    }

    const card = target.closest(".product-card-wrapper .card");

    if (!(card instanceof HTMLElement) || card.dataset.lymiaAstroNavigating === "true") {
      return;
    }

    const link = getProductCardLink(card);

    if (!(link instanceof HTMLAnchorElement) || !link.href) {
      return;
    }

    const clickedLink = target.closest("a[href]");
    const isProductLinkClick = clickedLink instanceof HTMLAnchorElement && clickedLink.href === link.href;
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

    if (clickedLink && !isProductLinkClick) {
      return;
    }

    if (
      !clickedLink
      && target.closest("button, input, select, textarea, label, summary, [role='button'], .button, .quantity, .quick-add, .product-form")
    ) {
      return;
    }

    if (isModifiedClick || link.target && link.target !== "_self") {
      if (!clickedLink) {
        window.open(link.href, "_blank", "noopener");
      }

      return;
    }

    event.preventDefault();
    card.dataset.lymiaAstroNavigating = "true";
    createProductCardSparkleBurst(card, event);

    window.setTimeout(() => {
      window.location.assign(link.href);
    }, isAstroReduced() ? 0 : 220);
  };

  const closeSecretReveal = (overlay) => {
    stopSecretMusic();
    document.documentElement.classList.remove("lymia-astro-secret-open");
    overlay.classList.add("is-closing");

    window.setTimeout(() => {
      overlay.remove();
      window.setTimeout(placeSecretStar, 450);
    }, 260);
  };

  const spotlightSecretStar = (star) => {
    const marker = document.createElement("div");
    const rect = star.getBoundingClientRect();

    marker.className = "lymia-astro-secret-guide";
    marker.innerHTML = "<span></span><span></span><span></span>";
    marker.style.left = `${rect.left + rect.width / 2}px`;
    marker.style.top = `${rect.top + rect.height / 2}px`;
    document.body.appendChild(marker);

    window.setTimeout(() => {
      marker.remove();
    }, 6500);
  };

  const guideToSecretStar = (reset = false) => {
    let star = document.querySelector(".lymia-astro-secret-star");

    if (!star && reset) {
      placeSecretStar();
      star = document.querySelector(".lymia-astro-secret-star");
    }

    if (!star) {
      console.info("L'etoile secrete n'est pas visible pour le moment. Relance lymiaAstroGuideSecretStar(true) pour la replacer temporairement.");
      return false;
    }

    spotlightSecretStar(star);
    star.classList.add("is-guided");
    window.setTimeout(() => star.classList.remove("is-guided"), 6500);

    return true;
  };

  const updateSecretPanel = (overlay, result) => {
    if (!document.body.contains(overlay)) {
      return;
    }

    const panel = overlay.querySelector(".lymia-astro-secret-panel");
    const eyebrow = overlay.querySelector(".lymia-astro-secret-eyebrow");
    const heading = overlay.querySelector(".lymia-astro-secret-heading");
    const message = overlay.querySelector(".lymia-astro-secret-message");
    const code = overlay.querySelector(".lymia-astro-secret-code");
    const loader = overlay.querySelector(".lymia-astro-secret-loader");

    panel.classList.remove("is-loading", "is-won", "is-lost", "is-error");
    panel.classList.add(`is-${result.state}`);
    loader.hidden = true;

    if (result.state === "won") {
      eyebrow.innerHTML = "Etoile valid&eacute;e";
      heading.innerHTML = "F&eacute;licitations, tu as trouv&eacute; le secret ! 🌠";
      message.innerHTML = "Avec ce code promo, tu bénéficies de 15€ de réduction sur une future commande ";
      code.textContent = secretCode;
      code.classList.remove("is-hidden");
      return;
    }

    if (result.state === "lost") {
      eyebrow.innerHTML = "Etoile d&eacute;j&agrave; r&eacute;clam&eacute;e";
      heading.innerHTML = "Dommage ! Quelqu&rsquo;un d&rsquo;autre a d&eacute;j&agrave; trouv&eacute; le secret avant toi 🌠";
      message.innerHTML = "Bravo d&rsquo;avoir trouvé tout de même !";
      code.textContent = "";
      code.classList.add("is-hidden");
      return;
    }

    eyebrow.innerHTML = "Signal perdu";
    heading.innerHTML = "Les &eacute;toiles n&rsquo;ont pas r&eacute;pondu.";
    message.innerHTML = "R&eacute;essaie dans quelques instants.";
    code.textContent = "";
    code.classList.add("is-hidden");
  };

  const showSecretReveal = () => {
    const previous = document.querySelector(".lymia-astro-secret-reveal");

    if (previous) {
      previous.remove();
    }

    const overlay = document.createElement("div");
    overlay.className = "lymia-astro-secret-reveal";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Code secret Lymia Astro");
    overlay.innerHTML = `
      <div class="lymia-astro-secret-sky" aria-hidden="true"></div>
      <div class="lymia-astro-secret-burst" aria-hidden="true"></div>
      <div class="lymia-astro-secret-panel is-loading" role="document">
        <div class="lymia-astro-secret-orbit" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <p class="lymia-astro-secret-eyebrow">Lecture des &eacute;toiles</p>
        <h2 class="lymia-astro-secret-heading">Un instant...</h2>
        <p class="lymia-astro-secret-message">Le ciel v&eacute;rifie si le code secret est encore disponible.</p>
        <div class="lymia-astro-secret-loader" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <strong class="lymia-astro-secret-code is-hidden"></strong>
        <button class="lymia-astro-secret-audio" type="button" hidden>Activer la musique</button>
        <button class="lymia-astro-secret-close" type="button">Fermer</button>
      </div>
    `;

    const sky = overlay.querySelector(".lymia-astro-secret-sky");
    const burst = overlay.querySelector(".lymia-astro-secret-burst");
    const audioButton = overlay.querySelector(".lymia-astro-secret-audio");
    const closeButton = overlay.querySelector(".lymia-astro-secret-close");

    for (let index = 0; index < (isAstroReduced() ? 24 : 96); index += 1) {
      createRevealStar(sky);
    }

    for (let index = 0; index < (isAstroReduced() ? 0 : 18); index += 1) {
      createBurstStar(burst);
    }

    audioButton.addEventListener("click", () => {
      playSecretMusic(audioButton);
    });
    closeButton.addEventListener("click", () => closeSecretReveal(overlay));
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) {
        closeSecretReveal(overlay);
      }
    });
    document.addEventListener("keydown", function handleEscape(event) {
      if (event.key === "Escape" && document.body.contains(overlay)) {
        closeSecretReveal(overlay);
        document.removeEventListener("keydown", handleEscape);
      }
    });

    document.body.appendChild(overlay);
    document.documentElement.classList.add("lymia-astro-secret-open");
    closeButton.focus({ preventScroll: true });

    if (!isAstroReduced()) {
      playSecretMusic(audioButton);
    }

    const secretStatePromise = fetchSecretState().then((result) => {
      if (result.state === "won") {
        claimSecretState(result.data);
      }

      return result;
    });

    Promise.all([secretStatePromise, delay(1750)]).then(([result]) => {
      updateSecretPanel(overlay, result);
    });
  };

  const clampSecretStar = (star) => {
    const rect = star.getBoundingClientRect();
    let left = clamp(rect.left + rect.width / 2, 42, Math.max(52, width - 52));
    let top = clamp(rect.top + rect.height / 2, 118, Math.max(132, height - 58));

    if (isUnsafeSecretStarTarget(left, top, star)) {
      const position = getSafeSecretStarPosition(star);

      left = position.left;
      top = position.top;
    }

    star.style.left = `${left}px`;
    star.style.top = `${top}px`;
  };

  const placeSecretStar = () => {
    if (document.querySelector(".lymia-astro-secret-star") || document.querySelector(".lymia-astro-secret-reveal")) {
      return;
    }

    const star = document.createElement("button");
    star.type = "button";
    star.className = "lymia-astro-secret-star";
    star.setAttribute("aria-label", "Etoile secrete");
    star.innerHTML = "<span aria-hidden=\"true\">&#10022;</span><span aria-hidden=\"true\">&#10023;</span>";

    const { left, top } = getSafeSecretStarPosition();

    star.style.left = `${left}px`;
    star.style.top = `${top}px`;

    star.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      star.remove();
      showSecretReveal();
    });

    document.body.appendChild(star);
  };

  const drawShootingStar = (shootingStar, delta) => {
    shootingStar.life += delta;

    const progress = shootingStar.life / shootingStar.ttl;
    const alpha = Math.sin(Math.min(progress, 1) * Math.PI);
    const distance = shootingStar.speed * shootingStar.life;
    const x = shootingStar.x - distance;
    const y = shootingStar.y + distance * 0.34;
    const tailX = x + shootingStar.length;
    const tailY = y - shootingStar.length * 0.34;
    const gradient = context.createLinearGradient(x, y, tailX, tailY);

    updateShootingStarHit(shootingStar, x, y, alpha);

    gradient.addColorStop(0, `rgba(${shootingStar.color}, ${0.9 * alpha})`);
    gradient.addColorStop(0.18, `rgba(${shootingStar.accent}, ${0.55 * alpha})`);
    gradient.addColorStop(0.42, `rgba(${shootingStar.color}, ${0.24 * alpha})`);
    gradient.addColorStop(1, `rgba(${shootingStar.color}, 0)`);

    context.save();
    context.globalCompositeOperation = "lighter";

    const halo = context.createRadialGradient(x, y, 0, x, y, 72);

    halo.addColorStop(0, `rgba(${shootingStar.color}, ${0.54 * alpha})`);
    halo.addColorStop(0.34, `rgba(${shootingStar.accent}, ${0.2 * alpha})`);
    halo.addColorStop(1, `rgba(${shootingStar.color}, 0)`);

    context.fillStyle = halo;
    context.beginPath();
    context.arc(x, y, 72, 0, Math.PI * 2);
    context.fill();

    context.lineCap = "round";
    context.strokeStyle = gradient;
    context.lineWidth = 3.6;
    context.shadowColor = `rgba(${shootingStar.color}, ${0.9 * alpha})`;
    context.shadowBlur = 34;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(tailX, tailY);
    context.stroke();

    context.fillStyle = `rgba(255, 255, 255, ${0.95 * alpha})`;
    context.shadowColor = `rgba(${shootingStar.color}, ${0.95 * alpha})`;
    context.shadowBlur = 26;
    context.font = "22px serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(shootingStar.glyph, x, y);
    context.restore();
  };

  const render = (timestamp) => {
    if (isAstroReduced()) {
      renderFrame = 0;
      renderStaticSky();
      return;
    }

    const seconds = timestamp / 1000;
    const delta = Math.min(0.04, (timestamp - lastFrame) / 1000 || 0.016);
    lastFrame = timestamp;
    shootingTimer += delta;

    context.clearRect(0, 0, width, height);
    context.shadowBlur = 0;

    for (const star of stars) {
      drawStar(star, seconds);
    }

    if (shootingTimer > nextShootingStarDelay) {
      shootingTimer = 0;
      setNextShootingStarDelay();
      createShootingStar();
    }

    shootingStars = shootingStars.filter((shootingStar) => {
      const isAlive = shootingStar.life < shootingStar.ttl;

      if (!isAlive) {
        removeShootingStarHit(shootingStar);
      }

      return isAlive;
    });

    for (const shootingStar of shootingStars) {
      drawShootingStar(shootingStar, delta);
    }

    renderFrame = requestAnimationFrame(render);
  };

  resize();
  applyAstroLogo();
  applyAstroIconBoxImages();
  applyAstroInstagramIntro();
  decorateProductCards();
  installPerformanceToggle();
  setNextShootingStarDelay();
  setAstroReducedMode(astroReduced, false);
  window.lymiaAstroApplyLogo = applyAstroLogo;
  window.lymiaAstroApplyIconBoxImages = applyAstroIconBoxImages;
  window.lymiaAstroApplyInstagramIntro = applyAstroInstagramIntro;
  window.lymiaAstroDecorateCards = decorateProductCards;
  window.lymiaAstroClaimSecret = claimSecretState;
  window.lymiaAstroSetReducedMode = setAstroReducedMode;
  window.lymiaAstroGuideSecretStar = guideToSecretStar;
  window.lymiaAstroTestMusic = testSecretMusic;
  window.lymiaAstroTriggerShootingShower = triggerShootingStarShower;
  window.lymiaAstroSummonShootingStar = createShootingStar;
  window.addEventListener("resize", resize, { passive: true });
  document.addEventListener("click", handleProductCardClick);
  document.addEventListener("click", (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest(
      ".product-form__submit, button[name='add'], form[action*='/cart/add'] button[type='submit']",
    );

    if (!button || !(button instanceof HTMLButtonElement)) {
      return;
    }

    createCartSparkleBurst(button, event);
  });
  new MutationObserver((mutations) => {
    const shouldRefreshAstroBlocks = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      return node.matches?.(".product-card-wrapper, .collection-card-wrapper, .card, .sp-icon-box-1, .sp-section-icon-box-1, #rap__med__instagram, #rap__med__insta")
        || Boolean(node.querySelector?.(".product-card-wrapper .card, .collection-card-wrapper .card, .sp-section-icon-box-1, #rap__med__instagram, #rap__med__insta"));
    }));

    if (shouldRefreshAstroBlocks) {
      scheduleProductCardDecoration();
      applyAstroIconBoxImages();
      applyAstroInstagramIntro();
    }

    if (!performanceToggle || !document.body.contains(performanceToggle)) {
      installPerformanceToggle();
    }
  }).observe(document.body, { childList: true, subtree: true });
  window.setTimeout(placeSecretStar, 750);
})();
